import React, { useMemo } from "react";
import InventoryKPI from "./InventoryKPI";
import { GET_INVENTORY_CODE_REPOSITORIES } from "../../../WidgetDataApi";
import useQueryWithRetries from "../../../../Tools/useQueryWithRetries";
import { useIcon, formatNumber } from "@SeemplicityDev/shared";
import type { EntityItem } from "../../../../Components/EntityLogo/EntityList";
import type { SelectedScope } from "../../../../types/scope";

interface InventoryRepositoriesKPIProps {
  selectedScope?: SelectedScope | null;
  scopeToExecute?: string | null;
  filterToExecute?: string | null;
  isForExport?: boolean;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

type InventoryRepositoriesRow = {
  platform: string;
  resources_count: number;
};

const InventoryRepositoriesKPI: React.FC<InventoryRepositoriesKPIProps> = ({
  selectedScope,
  scopeToExecute,
  filterToExecute,
  isForExport,
  setIsReadyToExport = null,
}) => {
  const { data, error, loading, refetch } = useQueryWithRetries({
    query: GET_INVENTORY_CODE_REPOSITORIES,
    variables: {
      widget_definition_key: "inventory_code_repository_by_platform",
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const { getProviderIcon } = useIcon(isForExport ? "LIGHT" : undefined);

  const processedData = useMemo<EntityItem[] | null | undefined>(() => {
    if (!!data) {
      const rawData = (
        data as { generic_widget_data?: { data?: InventoryRepositoriesRow[] } }
      )?.generic_widget_data?.data;

      return rawData?.map((item): EntityItem => {
        const { platform, resources_count } = item;
        // getProviderIcon may return null; EntityItem.logo is typed string |
        // undefined but EntityList/EntityLogo handle a nullish logo identically.
        return {
          displayName: platform,
          logo: getProviderIcon(platform) as string | undefined,
          count: resources_count,
        };
      });
    }

    return null;
  }, [data, getProviderIcon]);

  return (
    <InventoryKPI
      isForExport={isForExport}
      data={processedData}
      error={error}
      loading={loading}
      refetch={refetch}
      tooltip={(provider) =>
        `${provider?.displayName}: ${formatNumber(provider?.count)} Code Resource${
          (provider?.count ?? 0) > 1 ? "s" : ""
        }`
      }
      setIsReadyToExport={setIsReadyToExport}
    />
  );
};

export default InventoryRepositoriesKPI;
